import React, { FC } from "react";
import Link from "designsystem/components/link";
import {
  Box,
  Card,
  CardImage,
  Text,
  TextProps,
  CardImageType,
  Icon,
  Icons,
  Grid,
  GridLayout
} from "designsystem";
import { IGatsbyImageData } from "gatsby-plugin-image";
import Markdown from "components/markdown";

const Title: FC<
  { children: string } & Pick<TextProps<HTMLElement>, "color" | "align">
> = ({ children, ...props }) => (
  <Text size="l" as="h3" {...props}>
    {children}
  </Text>
);

export interface CardProps {
  title?: string | null;
  metaTitle?: string | null;
  readonly childContentfulLinkCardBodyTextNode?: GatsbyTypes.Maybe<
    Pick<GatsbyTypes.contentfulLinkCardBodyTextNode, "body">
  >;
  url?: string | null;
  icon?: string | null;
  readonly image?: GatsbyTypes.ImageAssetNodeFragment | null;
  imageType?: string | null;
  readonly links?: ReadonlyArray<
    GatsbyTypes.LinkNodeFragment | undefined | null
  > | null;
}

const getLinkGrid = (length?: number): GridLayout => {
  if (length && length >= 3) {
    return "three-column-collapse";
  }
  if (length && length === 2) {
    return "two-column-collapse";
  }
  return "full-width";
};

/**
 * This component is used in the Content Page to render a link card
 * @param param0
 */
const LinkCard: React.FC<CardProps> = ({
  title,
  childContentfulLinkCardBodyTextNode,
  url,
  image,
  imageType,
  links,
  icon
}) => {
  const cardImage = image && {
    image: image.gatsbyImageData as unknown as IGatsbyImageData,
    alt: String(image.title)
  };

  const markdown = childContentfulLinkCardBodyTextNode?.body;
  const hasContent =
    title || (markdown && markdown.length > 0) || links?.length;
  const hasIcon = !!icon;

  const linkGrid = getLinkGrid(links?.length);

  return (
    <Card
      direction={imageType === "avatar" ? "row" : "column"}
      cardType={hasIcon ? "small-link-card" : "link-card"}
      border="none"
    >
      {cardImage && cardImage?.image?.images?.fallback && (
        <Link href={url ?? undefined}>
          <CardImage
            alt={cardImage.alt}
            src={cardImage.image.images.fallback.src}
            srcSet={cardImage.image.images.fallback.srcSet}
            type={(imageType as CardImageType) || "cover"}
          />
        </Link>
      )}
      {hasContent && (
        <Box
          padding="m"
          direction="column"
          border={cardImage ? "top" : undefined}
        >
          <Box
            justifyContent={
              url && title && !markdown && !hasIcon ? "center" : "start"
            }
          >
            {hasIcon && <Icon type={icon as Icons} marginRight="xs" />}
            {(url && title) ||
            (url &&
              title &&
              (!links || (links && links.length === 0)) &&
              hasIcon) ? (
              <Link href={url}>
                <Title color="link">{title}</Title>
              </Link>
            ) : undefined}
            {(!url && title) ||
            (title && hasIcon && links && links.length > 0) ? (
              <Title>{title}</Title>
            ) : undefined}
          </Box>
          {markdown ? <Markdown content={markdown} /> : undefined}
          {links && (
            <React.Fragment>
              {hasIcon ? (
                <Grid layout={linkGrid} gap="xxs" marginTop="text">
                  {links?.map(
                    (link) =>
                      link && (
                        <Link
                          href={link.url ?? undefined}
                          key={link.id}
                          target={link.openInNewWindow ? "_blank" : undefined}
                        >
                          <Box as="span">
                            <Icon type="arrow" marginRight="xs" />
                            {link.text}
                          </Box>
                        </Link>
                      )
                  )}
                </Grid>
              ) : (
                links?.map(
                  (link) =>
                    link && (
                      <Link
                        key={link.id}
                        href={link.url ?? undefined}
                        target={link.openInNewWindow ? "_blank" : undefined}
                      >
                        {link.text}
                      </Link>
                    )
                )
              )}
            </React.Fragment>
          )}
        </Box>
      )}
    </Card>
  );
};
export default LinkCard;
